/**
 * Marketing-tag loader — installs Google Tag Manager + CookieYes
 * (Consent Mode v2 banner) at runtime, only on the marketing
 * surface.
 *
 * Gating layers (all must pass):
 *   1. `surface === "marketing"` — never loads on the operator app,
 *      tenant portal, or operator-owned rental sites. The dataLayer
 *      gets a sentinel push from analytics.enable() but no GTM
 *      container script ever runs there, so a misconfigured tag in
 *      GTM can't accidentally fire conversions for an operator's
 *      apartment-tenant page view.
 *   2. `import.meta.env.PROD` — dev / UAT loops don't pollute the
 *      production GTM container.
 *   3. `VITE_GTM_CONTAINER_ID` env present — the build pulls the id
 *      from the K8s secret at image-build time. Missing key = no
 *      load. Useful for the first deploy before the GTM container
 *      is even created.
 *
 * Consent Mode v2 defaults are declared inline in index.html so they
 * apply BEFORE GTM loads (otherwise the first tag fires before the
 * banner has a chance to say "denied" by default). This loader only
 * has to inject the container and the consent-banner script.
 */
import { enable as enableAnalytics } from "./analytics";

interface Env {
  PROD?: boolean;
  VITE_GTM_CONTAINER_ID?: string;
  VITE_COOKIEYES_ID?: string;
  VITE_META_PIXEL_ID?: string;
  VITE_LINKEDIN_PARTNER_ID?: string;
}

const env = (import.meta as ImportMeta & { env: Env }).env;

export function installMarketingTags(surface: string): void {
  if (typeof window === "undefined") return;
  if (surface !== "marketing") return;
  if (!env.PROD) return;

  // Order matters: enable() the dataLayer wrapper FIRST so any push
  // that happens during the GTM container's bootstrap (it'll fire its
  // own initialization events) is captured even if the snippet races
  // the React tree.
  enableAnalytics();

  const gtmId = (env.VITE_GTM_CONTAINER_ID ?? "").trim();
  if (gtmId) {
    injectGtm(gtmId);
  }

  // Native Google Ads tag (AW-…). GTM never had a Google Ads
  // conversion tag configured, so the account's WEBPAGE conversion
  // actions never fired and Ads recorded zero conversions despite real
  // signups. Loading the Google tag directly + firing gtag('event',
  // 'conversion', …) from trackConversion (analytics.ts) gives the
  // same reliable native tracking AdPlus's own account uses — no GTM
  // edit required, and immune to the fragile GA4-import path.
  injectGoogleAds();

  const cookieYesId = (env.VITE_COOKIEYES_ID ?? "").trim();
  if (cookieYesId) {
    injectCookieYes(cookieYesId);
  }

  // Meta (Facebook) Pixel — only when a pixel id is configured. Left
  // empty by default, so the whole Meta path (browser pixel here + the
  // server-side Conversions API in the API) ships dormant until a real
  // NestPlot pixel is provisioned. We deliberately manage the pixel in
  // code (not GTM) so the browser event and the server CAPI event can
  // share the same event_id for deduplication.
  const metaPixelId = (env.VITE_META_PIXEL_ID ?? "").trim();
  if (metaPixelId) {
    injectMetaPixel(metaPixelId);
  }

  // LinkedIn Insight Tag. Creating the tag required certifying it would not
  // be installed on pages holding sensitive data — the tenant portal shows
  // people's addresses and rent payments. The `surface === "marketing"` gate
  // above is exactly that guarantee, which is why the tag is loaded here and
  // not from index.html: index.html is shared by every surface.
  const liPartnerId = (env.VITE_LINKEDIN_PARTNER_ID ?? "").trim();
  if (liPartnerId) {
    injectLinkedInInsight(liPartnerId);
  }
}

/** LinkedIn Insight Tag — powers conversion tracking and retargeting for the
 * NestPlot ad account. Kept in code rather than GTM for the same reason as
 * the Meta pixel: the browser event and any later server-side call need to
 * agree, and a half-configured GTM container silently records nothing. */
function injectLinkedInInsight(partnerId: string): void {
  const w = window as unknown as {
    _linkedin_partner_id?: string;
    _linkedin_data_partner_ids?: string[];
    lintrk?: ((a: unknown, b?: unknown) => void) & { q?: unknown[][] };
  };
  if (w.lintrk) return; // already installed
  w._linkedin_partner_id = partnerId;
  w._linkedin_data_partner_ids = w._linkedin_data_partner_ids || [];
  w._linkedin_data_partner_ids.push(partnerId);
  const shim = ((a: unknown, b?: unknown) => {
    shim.q!.push([a, b]);
  }) as ((a: unknown, b?: unknown) => void) & { q?: unknown[][] };
  shim.q = [];
  w.lintrk = shim;
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
  document.head.appendChild(script);
}

/** The Google Ads conversion account for nestplot.com (5868004084).
 * Account-wide id; individual conversion actions add a per-action
 * label in analytics.ts. */
export const GOOGLE_ADS_ID = "AW-18014180022";

/** Load the Google tag (gtag.js) for the Ads account and configure it.
 * gtag shares window.dataLayer with GTM — both coexist. The inline
 * consent-mode defaults in index.html already ran, so this tag honors
 * them (US granted, EEA/UK/CH denied-until-consent). */
function injectGoogleAds(): void {
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // gtag() pushes its argument list onto the dataLayer; gtag.js drains
  // it once loaded. Matches the canonical stub in index.html.
  window.dataLayer.push(["js", new Date()]);
  window.dataLayer.push(["config", GOOGLE_ADS_ID]);
}

/** Standard GTM JS snippet, programmatic. The official HTML snippet
 * blocks rendering with an inline `<script>`; injecting it at runtime
 * after the React shell is parsed avoids the LCP hit and lets the
 * surface gate above stop it from running where it shouldn't. */
function injectGtm(containerId: string): void {
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  document.head.appendChild(script);

  // The `gtm.start` push is what the GTM snippet does inline. Keep it
  // so the container's own timing measurements (e.g. "Time to GTM
  // ready") aren't broken.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });

  // No <noscript> iframe — we ship a SPA. JavaScript-disabled users
  // can't use the app at all, so the iframe fallback is dead weight.
}

/** Meta Pixel loader — the canonical fbevents.js snippet, run at
 * runtime after the surface gate. `fbq` becomes available globally;
 * analytics.ts fires CompleteRegistration / Lead through it with the
 * same event_id the server-side CAPI call uses. */
interface FbqFn {
  (...args: unknown[]): void;
  callMethod?: (...a: unknown[]) => void;
  queue: unknown[];
  loaded: boolean;
  version: string;
  push: unknown;
}

function injectMetaPixel(pixelId: string): void {
  const w = window as unknown as { fbq?: FbqFn; _fbq?: unknown };
  if (w.fbq) return;
  const fbq = ((...args: unknown[]) => {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  }) as FbqFn;
  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.push = fbq;
  w.fbq = fbq;
  w._fbq = w._fbq || fbq;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);

  w.fbq("init", pixelId);
  w.fbq("track", "PageView");
}

/** CookieYes loader. The script reads its config from the data attr
 * and renders the banner; once the user grants consent, CookieYes
 * fires `gtag('consent', 'update', …)` which GTM listens for and
 * unblocks the previously-denied tags. No further wiring needed. */
function injectCookieYes(id: string): void {
  const script = document.createElement("script");
  script.id = "cookieyes";
  script.src = `https://cdn-cookieyes.com/client_data/${encodeURIComponent(id)}/script.js`;
  document.head.appendChild(script);
}
